/**
 * Centralised public-facing SEO config.
 *
 * Why this lives in one file: it lets us inject identical business details
 * across <meta>, Open Graph, Twitter cards, and the JSON-LD structured data
 * blocks without ever duplicating strings. The siteUrl is overridden by
 * REACT_APP_SITE_URL at build time so non-prod environments don't pollute
 * Google's index with preview URLs.
 *
 * Public-facing UI strings are NOT changed by anything in this module — it
 * only emits hidden metadata and JSON-LD.
 */

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || 'https://cravincrispy.co.uk'
).replace(/\/$/, '');

export const BUSINESS = {
  name: 'Cravin Crrispy',
  legalName: 'Cravin Crrispy Forfar',
  url: SITE_URL,
  logoUrl: `${SITE_URL}/og-image.png`, // 1200x630 social/share image (referenced by OG, Twitter, JSON-LD)
  description:
    'Cravin Crrispy Forfar — fresh chicken, burgers, wraps and sides for fast delivery and collection across Forfar and surrounding postcodes.',
  telephone: '+441307477786',
  email: 'kmy.forfar@gmail.com',
  priceRange: '£10-£20',
  servesCuisine: ['Fried Chicken', 'Burgers', 'Kebabs', 'Wraps', 'Fast Food', 'Desserts', 'British'],
  menuUrl: `${SITE_URL}/#menu`,
  address: {
    streetAddress: '3-5 Queen St',
    addressLocality: 'Forfar',
    addressRegion: 'Angus',
    postalCode: 'DD8 3AJ',
    addressCountry: 'GB',
  },
  // Exact coordinates for 3-5 Queen St, Forfar — confirmed by the owner
  // from the Google Maps pin (right-click → coordinates). Kept in the
  // JSON-LD Restaurant graph via geo.
  geo: { latitude: 56.64517558167772, longitude: -2.8882559664133107 },
  openingHours: [
    // Schema.org openingHoursSpecification format — dayOfWeek + 24h times.
    { dayOfWeek: 'Monday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Tuesday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Wednesday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Thursday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Friday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Saturday', opens: '16:00', closes: '23:00' },
    { dayOfWeek: 'Sunday', opens: '16:00', closes: '23:00' },
  ],
  // Service flags consumed by Restaurant / LocalBusiness schema below.
  hasDelivery: true,
  hasTakeaway: true, // Collection
  areaServed: ['Forfar', 'Kirriemuir', 'Glamis', 'Letham', 'Angus', 'DD8'],
  // Off-site profiles for the schema.org `sameAs` property (NAP consistency).
  // Owner-confirmed URLs ONLY — verified from the owner brief. Third-party
  // directory listings (foodhub, restaurantji, FSA rating page) intentionally
  // left out until the owner explicitly claims/verifies each profile.
  sameAs: [
    'https://www.facebook.com/cravincrrispyforfar',
    'https://www.instagram.com/cravincrispyforfar/',
    'https://maps.app.goo.gl/TcMovj4qh39PvDqE8',
  ],
};

/**
 * Homepage FAQ content — single source of truth.
 *
 * The visible React component AND the FAQPage JSON-LD both read this
 * array. Divergence between rendered HTML and schema is a Google Rich
 * Results defect (and equally bad for AI-answer citations), so we
 * deliberately eliminate the risk by co-locating the data.
 *
 * Order matters — halal question is placed first per the owner brief
 * (high-volume local query with no competing answer in Forfar).
 */
export const HOMEPAGE_FAQS = [
  {
    q: 'What time does Cravin Crrispy open?',
    a: 'Cravin Crrispy opens at 4pm (16:00) every day of the week and serves until 11pm (23:00). Preorders are accepted from 11am daily, so you can place your order in the morning and have it prepared for the evening.',
  },
  {
    q: 'Does Cravin Crrispy deliver to my area?',
    a: 'We deliver across Forfar and the DD8 postcode area, including Kirriemuir, Glamis, Letham and nearby Angus villages. Enter your postcode at checkout to see the delivery fee and minimum order for your address. Collection from 3-5 Queen Street, Forfar is also available.',
  },
  {
    q: 'How do I order from Cravin Crrispy?',
    a: "Order online at cravincrispy.co.uk by browsing the menu and adding items to your basket, then choose delivery or collection at checkout. You can also call 01307 477786 to place an order. You'll receive a confirmation with your estimated preparation time.",
  },
  {
    q: 'Does Cravin Crrispy accept cash?',
    a: 'Yes. You can pay securely by card when ordering online, or choose cash on delivery or cash on collection at checkout. Card is the fastest option for delivery orders, but cash is always accepted when your food arrives or when you collect in store.',
  },
  {
    q: 'Can I place an order before opening time?',
    a: 'Yes — preorders are accepted from 11am every day. Place your order online in the morning and it will be prepared once the kitchen opens at 4pm. Customers cannot select a specific later delivery or collection time; orders are prepared in the order they are received after the kitchen opens.',
  },
  {
    q: 'Does Cravin Crrispy cater for allergies?',
    a: 'Customers with a food allergy should add a note to their online order and call the restaurant on 01307 477786 before ordering. Staff can check ingredient information, but cross-contamination cannot be completely excluded.',
  },
];

/**
 * Search-engine verification meta values. Leave the values blank in .env to
 * skip the tag entirely — both Google Search Console and Bing Webmaster
 * accept HTML-meta-tag verification this way without any redeploy needed
 * later, you just set the env var and redeploy.
 */
export const VERIFICATION = {
  google: process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || '',
  bing: process.env.REACT_APP_BING_SITE_VERIFICATION || '',
  yandex: process.env.REACT_APP_YANDEX_SITE_VERIFICATION || '',
  facebook: process.env.REACT_APP_FB_DOMAIN_VERIFICATION || '',
};
